/**
 * CSV Actions
 * Persisted CSV data management per floor (stored in floor.mapData.csv).
 * In-memory CSV runtime state has moved to csvStore.
 */

import { getCurrentTimestamp } from '../../lib/utils';
import { log, LogCategory } from '../../lib/logger';
import { FloorId } from '../../types/branded';
import type { CSVData, Floor, FloorState } from './types';
import { persistState } from './persistence';

type SetFn = (partial: Partial<FloorState>) => void;
type GetFn = () => FloorState;

export const createCsvActions = (set: SetFn, get: GetFn) => ({
  // --- Persisted CSV data actions ---

  setCSVData: (floorId: FloorId | string, csvData: CSVData | null) => {
    const state = get();
    const floor = state.floors.find((f) => f.id === floorId);

    if (!floor) {
      throw new Error(`Floor with id "${floorId}" not found`);
    }

    const updatedFloor: Floor = {
      ...floor,
      mapData: {
        ...floor.mapData,
        csv: csvData,
      },
      modified: getCurrentTimestamp(),
    };

    const newFloors = state.floors.map((f) => (f.id === floorId ? updatedFloor : f));

    persistState(newFloors, state.currentFloor);

    set({ floors: newFloors });

    log.info(LogCategory.FLOOR, 'CSV data updated', {
      floorId,
      fileName: csvData?.fileName,
    });
  },

  updateCSVData: (floorId: FloorId | string, updates: Partial<CSVData>) => {
    const state = get();
    const floor = state.floors.find((f) => f.id === floorId);

    if (!floor || !floor.mapData?.csv) {
      throw new Error(`Floor with id "${floorId}" not found or has no CSV data`);
    }

    const updatedCSV: CSVData = {
      ...floor.mapData.csv,
      ...updates,
    };

    const updatedFloor: Floor = {
      ...floor,
      mapData: {
        ...floor.mapData,
        csv: updatedCSV,
      },
      modified: getCurrentTimestamp(),
    };

    const newFloors = state.floors.map((f) => (f.id === floorId ? updatedFloor : f));

    persistState(newFloors, state.currentFloor);

    set({ floors: newFloors });
  },

  clearCSVData: (floorId: FloorId | string) => {
    get().setCSVData(floorId, null);
    log.info(LogCategory.FLOOR, 'CSV data cleared', { floorId });
  },

  getCSVData: (floorId: FloorId | string) => {
    const floor = get().floors.find((f) => f.id === floorId);
    return floor?.mapData?.csv ?? null;
  },
});
